import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Stethoscope,
  Activity,
  Heart,
  Thermometer,
  Weight,
  Ruler,
  CheckCircle,
  AlertTriangle,
  FileCheck2,
  FileText,
  User,
  Building,
  Briefcase,
  ShieldAlert,
  ExternalLink,
  ClipboardList,
  Clock,
  FlaskConical,
  ShieldCheck,
  Sparkles,
  Calendar,
  Eye,
  RefreshCw,
  Camera,
  Upload,
  RotateCcw,
} from 'lucide-react';
import { clinicaService } from '../services/clinica.service';
import { formatCpf, formatCnpj, formatDateBR } from '../utils/formatters';
import type {
  MedicoAtendimentoPendenteContract,
  MedicoPerfilParceiroContract,
  MedicoRealizarAtendimentoDto,
  MedicoRealizarAtendimentoResponse,
  MedicoAtendimentoDetalhesContract,
  MedicoAtendimentoExameItemContract,
} from '@sstexames/shared-types';

interface AtendimentoMedicoModalProps {
  isOpen: boolean;
  onClose: () => void;
  atendimento: MedicoAtendimentoPendenteContract;
  medico: MedicoPerfilParceiroContract;
  clinicaId: number;
  onSuccess: (response: MedicoRealizarAtendimentoResponse) => void;
  onOpenCertificadoModal: () => void;
  onReabrir?: (agendamentoId: number) => void;
  onForcarAso?: (agendamentoId: number) => Promise<void> | void;
  readOnly?: boolean;
}

interface QuestaoAnamnese {
  id: number;
  questao: string;
  ordem: number;
  tipo?: { id: number; nome: string; reference: string } | null;
}

interface FichaHeaderAnamnese {
  id: number;
  titulo_exibicao: string;
  descricao: string;
  ficha_questao: QuestaoAnamnese[];
}

interface FastTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string;
  onValueChange: (val: string) => void;
}

const FastTextarea: React.FC<FastTextareaProps> = ({ value, onValueChange, ...props }) => {
  const [val, setVal] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (value !== prevValue) {
    setPrevValue(value);
    setVal(value);
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setVal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onValueChange(next);
    }, 150);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    onValueChange(val);
    if (props.onBlur) props.onBlur(e);
  };

  return (
    <textarea
      {...props}
      value={val}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default function AtendimentoMedicoModal({
  isOpen,
  onClose,
  atendimento,
  medico,
  clinicaId,
  onSuccess,
  onOpenCertificadoModal,
  onReabrir,
  onForcarAso,
  readOnly,
}: AtendimentoMedicoModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gerandoAso, setGerandoAso] = useState(false);
  const [generatedAsoUrl, setGeneratedAsoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultadoFinal, setResultadoFinal] = useState<MedicoRealizarAtendimentoResponse | null>(null);
  const [detalhesCompletos, setDetalhesCompletos] = useState<MedicoAtendimentoDetalhesContract | null>(null);

  const effectiveAsoUrl = generatedAsoUrl || atendimento?.aso_url;
  const isReaberto = Boolean(
    detalhesCompletos?.valores_salvos?.is_reaberto ||
    ((atendimento as unknown) as Record<string, unknown>)?.is_reaberto
  );
  const isAguardando =
    Boolean(atendimento?.aguardando_exames) ||
    atendimento?.status === 'Aguardando Laudo' ||
    atendimento?.status === 'Aguardando Exames' ||
    atendimento?.status_operacional === 'AGENDADO_CLINICA' ||
    !effectiveAsoUrl;
  const isReadOnly = !isReaberto && (!!readOnly || (atendimento?.status === 'Realizado' && Boolean(effectiveAsoUrl) && !isAguardando));

  // Sinais vitais
  const [sistolica, setSistolica] = useState<string>('');
  const [diastolica, setDiastolica] = useState<string>('');
  const [fc, setFc] = useState<string>('');
  const [fr, setFr] = useState<string>('');
  const [temperatura, setTemperatura] = useState<string>('');
  const [peso, setPeso] = useState<string>('');
  const [altura, setAltura] = useState<string>('');

  // Aptidão e parecer
  const [valor, setValor] = useState<string>('APTO');
  const [observacao, setObservacao] = useState<string>('');

  // Questões da ficha anamnese
  const [headers, setHeaders] = useState<FichaHeaderAnamnese[]>([]);
  const [respostas, setRespostas] = useState<Record<number, { resposta: number; complemento: string }>>({});

  // Biometria Facial do Colaborador
  const [biometriaFoto, setBiometriaFoto] = useState<string | null>(null);
  const [biometriaTipo, setBiometriaTipo] = useState<'WEBCAM' | 'UPLOAD'>('WEBCAM');
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [visualizandoLaudoId, setVisualizandoLaudoId] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const handleVisualizarLaudo = async (resultadoId?: number | null, agendamentoId?: number) => {
    if (!resultadoId && !agendamentoId) {
      alert('Identificador do laudo não encontrado.');
      return;
    }

    const idToTrack = resultadoId || agendamentoId!;
    setVisualizandoLaudoId(idToTrack);
    try {
      let blob: Blob;
      if (resultadoId) {
        blob = await clinicaService.downloadLaudo(resultadoId);
      } else {
        blob = await clinicaService.downloadLaudoPorAgendamento(agendamentoId!);
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, '_blank');
      if (!opened) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `laudo_${idToTrack}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (e) {
      console.error('Erro ao carregar laudo:', e);
      alert('Não foi possível carregar o arquivo do laudo no momento.');
    } finally {
      setVisualizandoLaudoId(null);
    }
  };

  // Parar webcam ao desmontar ou fechar
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setCameraError(null);
      setIsCameraActive(true); // Ativa primeiro para renderizar o <video ref>

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;

      // Usar setTimeout para aguardar o ciclo de renderização do React
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.warn('Erro ao dar play no video:', e));
        }
      }, 100);
      
    } catch (err: unknown) {
      console.warn('Erro ao acessar webcam:', err);
      setCameraError(
        'Não foi possível inicializar a câmera. Verifique as permissões de acesso do navegador ou anexe uma foto diretamente.',
      );
      setIsCameraActive(false);
    }
  };

  const captureSnapshot = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    setBiometriaFoto(dataUrl);
    setBiometriaTipo('WEBCAM');
    stopCamera();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setBiometriaFoto(reader.result);
        setBiometriaTipo('UPLOAD');
        stopCamera();
      }
    };
    reader.readAsDataURL(file);
  };

  // Upload inline de laudo complementar removido a pedido do usuário (o médico apenas visualiza)

  // Cálculo automático do IMC
  const imcInfo = useMemo(() => {
    const p = parseFloat(peso.replace(',', '.'));
    const a = parseFloat(altura) / 100; // converter cm para m
    if (p > 0 && a > 0) {
      const imcVal = p / (a * a);
      const imcStr = imcVal.toFixed(1);
      let classificacao = 'Eutrófico';
      let badgeColor = 'bg-primary-100 text-primary-800 border-primary-300';

      if (imcVal < 18.5) {
        classificacao = 'Abaixo do Peso';
        badgeColor = 'bg-amber-100 text-amber-800 border-amber-300';
      } else if (imcVal >= 25 && imcVal < 30) {
        classificacao = 'Sobrepeso';
        badgeColor = 'bg-amber-100 text-amber-800 border-amber-300';
      } else if (imcVal >= 30 && imcVal < 35) {
        classificacao = 'Obesidade Grau I';
        badgeColor = 'bg-orange-100 text-orange-800 border-orange-300';
      } else if (imcVal >= 35 && imcVal < 40) {
        classificacao = 'Obesidade Grau II';
        badgeColor = 'bg-rose-100 text-rose-800 border-rose-300';
      } else if (imcVal >= 40) {
        classificacao = 'Obesidade Grau III';
        badgeColor = 'bg-rose-200 text-rose-900 border-rose-400 font-bold';
      }

      return { imc: imcStr, classificacao, badgeColor };
    }
    return null;
  }, [peso, altura]);

  // Carregar detalhes completos (colaborador, exames e questões da anamnese)
  useEffect(() => {
    if (!isOpen || !atendimento?.id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    setResultadoFinal(null);

    clinicaService
      .getAtendimentoDetalhes(atendimento.agendamento_id || atendimento.id, clinicaId)
      .then(async (detalhes) => {
        setDetalhesCompletos(detalhes);
        
        if (detalhes?.valores_salvos) {
          const vs = detalhes.valores_salvos;
          if (vs.sistolica != null) setSistolica(vs.sistolica.toString());
          if (vs.diastolica != null) setDiastolica(vs.diastolica.toString());
          if (vs.fc != null) setFc(vs.fc.toString());
          if (vs.fr != null) setFr(vs.fr.toString());
          if (vs.temperatura != null) setTemperatura(vs.temperatura.toString());
          if (vs.peso != null) setPeso(vs.peso.toString());
          if (vs.altura != null) setAltura(vs.altura.toString());
          if (vs.valor && vs.valor.trim() !== '') {
            setValor(vs.valor.trim());
          } else {
            setValor('APTO');
          }
          if (vs.observacao != null) setObservacao(vs.observacao);
          if (
            vs.biometria_facial_url &&
            (vs.biometria_facial_url.startsWith('data:image/') ||
              vs.biometria_facial_url.startsWith('blob:') ||
              vs.biometria_facial_url.startsWith('http'))
          ) {
            setBiometriaFoto(vs.biometria_facial_url);
            setBiometriaTipo('UPLOAD');
          } else if (vs.biometria_facial_url || vs.biometria_coletada) {
            try {
              const targetAgendamentoId = atendimento.agendamento_id || atendimento.id;
              const blob = await clinicaService.downloadBiometria(targetAgendamentoId);
              const objectUrl = URL.createObjectURL(blob);
              setBiometriaFoto(objectUrl);
              setBiometriaTipo('UPLOAD');
            } catch (err) {
              console.warn('Não foi possível carregar biometria via downloadBiometria:', err);
              if (vs.biometria_facial_url && vs.biometria_facial_url.startsWith('http')) {
                setBiometriaFoto(vs.biometria_facial_url);
              } else {
                setBiometriaFoto('https://ui-avatars.com/api/?name=Biometria+Coletada&background=random');
              }
              setBiometriaTipo('UPLOAD');
            }
          }
        } else {
          setSistolica(''); setDiastolica(''); setFc(''); setFr(''); 
          setTemperatura(''); setPeso(''); setAltura(''); setValor('APTO'); setObservacao('');
          setBiometriaFoto(null); setBiometriaTipo('WEBCAM');
        }

        if (detalhes?.questoes) {
          setHeaders(detalhes.questoes as FichaHeaderAnamnese[]);
          // Inicializar respostas
          const initial: Record<number, { resposta: number; complemento: string }> = {};
          const savedRespostas = detalhes.valores_salvos?.respostas || [];
          const savedMap = new Map(savedRespostas.map((r) => [r.questao_id, { resposta: r.resposta, complemento: r.complemento || '' }]));

          detalhes.questoes.forEach((h: FichaHeaderAnamnese) => {
            h.ficha_questao?.forEach((q) => {
              if (savedMap.has(q.id)) {
                const s = savedMap.get(q.id)!;
                initial[q.id] = {
                  resposta: Number(s.resposta),
                  complemento: s.complemento || '',
                };
              } else {
                initial[q.id] = { resposta: 0, complemento: '' };
              }
            });
          });
          setRespostas(initial);
        }
      })
      .catch((err) => {
        console.warn('Erro ao carregar detalhes do atendimento:', err);
        setError('Não foi possível carregar os dados completos do atendimento.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, atendimento, clinicaId]);

  if (!isOpen) return null;

  const handleRespostaChange = (questaoId: number, resp: number) => {
    setRespostas((prev) => ({
      ...prev,
      [questaoId]: {
        resposta: resp,
        complemento: resp === 0 ? '' : prev[questaoId]?.complemento || '',
      },
    }));
  };

  const handleComplementoChange = (questaoId: number, comp: string) => {
    setRespostas((prev) => ({
      ...prev,
      [questaoId]: {
        ...prev[questaoId],
        complemento: comp,
      },
    }));
  };

  // Agilidade médica: Marcar todas as questões como "Não"
  const handleMarcarTodasNao = () => {
    const updated: Record<number, { resposta: number; complemento: string }> = {};
    headers.forEach((h) => {
      h.ficha_questao?.forEach((q) => {
        updated[q.id] = { resposta: 0, complemento: '' };
      });
    });
    setRespostas(updated);
  };

  // Dados unificados do colaborador (prioriza detalhesCompletos da API)
  const colaborador = detalhesCompletos?.colaborador || atendimento.colaborador;
  const listaExames: MedicoAtendimentoExameItemContract[] = (
    detalhesCompletos?.exames || [
      {
        id: atendimento.agendamento_id || atendimento.id,
        nome: `Exame Clínico Ocupacional (${atendimento.tipo_exame})`,
        tipo: 'CLINICO',
        status: 'PENDENTE',
        obrigatorio: true,
      },
    ]
  ).filter((e) => {
    const n = (e.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (n.includes('psicossocial')) return false;
    if (
      e.tipo !== 'CLINICO' &&
      (n.includes('admissional') ||
        n.includes('periodico') ||
        n.includes('demissional') ||
        n.includes('retorno') ||
        n.includes('mudanca'))
    ) {
      return false;
    }
    return true;
  });

  const examesComplementaresPendentes = listaExames.filter(
    (e) =>
      e.tipo !== 'CLINICO' &&
      e.status !== 'REALIZADO' &&
      e.status !== 'CONCLUIDO' &&
      !e.resultado_id &&
      !e.resultado_url &&
      !e.nome.toUpperCase().includes('PSICOSSOCIAL'),
  );
  const temExamesPendentes = examesComplementaresPendentes.length > 0;
  const isModificacao = !!(
    detalhesCompletos?.valores_salvos?.is_reaberto ||
    detalhesCompletos?.valores_salvos?.is_concluido
  );
  const biometriaColetadaAnteriormente = !!detalhesCompletos?.valores_salvos?.biometria_coletada;
  const exigeBiometriaConsulta = !!detalhesCompletos?.agendamento?.biometria_facial_consulta;
  const exigeCertificadoA1 = detalhesCompletos?.agendamento?.exige_certificado_a1 !== false;

  const handleSubmit = async (e?: React.FormEvent, forcarAguardar = false) => {
    if (e) e.preventDefault();

    if (exigeCertificadoA1 && !medico.has_valid_certificate) {
      setError(
        'O médico examinador precisa ter o Certificado Digital A1 vinculado e ativo para assinar digitalmente o ASO e concluir o atendimento.',
      );
      return;
    }

    if (exigeBiometriaConsulta && !biometriaFoto && !biometriaColetadaAnteriormente) {
      setError(
        'A biometria facial do colaborador é obrigatória para finalizar o atendimento. Capture a foto via câmera ou anexe um arquivo para comprovação de ciência.',
      );
      return;
    }

    const valorEfetivo = forcarAguardar ? 'AGUARDANDO_EXAMES' : (valor || 'APTO');

    if (valorEfetivo !== 'AGUARDANDO_EXAMES' && valorEfetivo !== 'APTO' && (!observacao || !observacao.trim())) {
      setError(
        'As observações médicas são obrigatórias quando o resultado for diferente de Apto (justificativa clínica ou restrições).',
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    const questoesPayload = Object.entries(respostas).map(([qid, val]) => ({
      questao_id: Number(qid),
      resposta: val.resposta,
      complemento: val.complemento || undefined,
    }));

    const dto: MedicoRealizarAtendimentoDto = {
      agendamento_id: atendimento.agendamento_id || atendimento.id,
      clinica_id: clinicaId,
      data_realizacao: new Date().toISOString().split('T')[0],
      valor: valorEfetivo,
      observacao: observacao || undefined,
      sistolica: sistolica ? parseInt(sistolica, 10) : undefined,
      diastolica: diastolica ? parseInt(diastolica, 10) : undefined,
      temperatura: temperatura ? parseFloat(temperatura.replace(',', '.')) : undefined,
      fc: fc ? parseInt(fc, 10) : undefined,
      fr: fr ? parseInt(fr, 10) : undefined,
      peso: peso ? parseFloat(peso.replace(',', '.')) : undefined,
      altura: altura ? parseInt(altura, 10) : undefined,
      imc: imcInfo?.imc ? parseFloat(imcInfo.imc) : undefined,
      questoes: questoesPayload,
      biometria_foto: biometriaFoto,
      biometria_tipo: biometriaTipo,
    };

    try {
      const response = await clinicaService.realizarAtendimento(dto);
      setResultadoFinal(response);
      onSuccess(response);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Erro ao processar atendimento e assinar ASO.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForcarGerarAsoLocal = async () => {
    const agendamentoId = atendimento.agendamento_id || atendimento.id;
    try {
      setGerandoAso(true);
      setError(null);
      if (onForcarAso) {
        await onForcarAso(agendamentoId);
      } else {
        const res = await clinicaService.forcarGerarAso(agendamentoId);
        if (res.aso_url) {
          setGeneratedAsoUrl(res.aso_url);
        }
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Falha ao gerar e assinar ASO. Verifique se o certificado A1 está ativo e tente novamente.';
      setError(msg);
    } finally {
      setGerandoAso(false);
    }
  };

  const riscos = detalhesCompletos?.riscos;
  const temRiscos =
    riscos &&
    (riscos.fisico?.length > 0 ||
      riscos.quimico?.length > 0 ||
      riscos.biologico?.length > 0 ||
      riscos.ergonomico?.length > 0 ||
      riscos.acidente?.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-900/75 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden border border-slate-200 flex flex-col max-h-[94vh]">
        {/* Header Superior */}
        <div className="px-6 py-4 bg-primary-600 text-white flex items-center justify-between shrink-0 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 shadow-xs">
              <Stethoscope className="w-6 h-6 text-primary-200" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold">Atendimento Médico Ocupacional</h2>
                <span className="px-2 py-0.5 rounded-full text-2xs font-bold bg-white/20 text-white border border-white/20">
                  {atendimento.tipo_exame}
                </span>
              </div>
              <p className="text-xs text-primary-100 flex items-center gap-1.5 mt-0.5">
                <span>Dr(a). {medico.nome}</span>
                <span>•</span>
                <span>CRM/{medico.uf} {medico.crm}</span>
                {medico.rqe && <span>(RQE {medico.rqe})</span>}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/20 transition-colors text-white cursor-pointer"
            title="Fechar Janela"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mensagem de Erro Fixa no Topo */}
        {error && (
          <div className="px-6 py-3 bg-rose-50 border-b border-rose-200 text-rose-900 text-xs flex items-center gap-3 shadow-sm shrink-0 z-10 relative animate-fadeIn">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
            <div className="flex-1">
              <p className="font-bold">Atenção</p>
              <p className="mt-0.5">{error}</p>
            </div>
            <button type="button" onClick={() => setError(null)} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-700 transition-colors cursor-pointer" title="Ocultar aviso">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Corpo Principal com Scroll */}
        <div className="p-6 overflow-y-auto space-y-6">
          {loading && (
            <div className="py-8 flex flex-col items-center justify-center space-y-3">
              <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs font-semibold text-slate-500">
                Carregando prontuário, exames previstos e ficha de anamnese...
              </p>
            </div>
          )}

          {/* Sucesso Final ou Aguardando Exames */}
          {resultadoFinal && (
            <div
              className={`p-6 rounded-2xl border-2 space-y-4 shadow-sm animate-fadeIn ${
                resultadoFinal.aguardando_exames
                  ? 'bg-amber-50 border-amber-300 text-amber-950'
                  : 'bg-primary-50 border-primary-300 text-primary-950'
              }`}
            >
              <div className="flex items-center gap-3">
                {resultadoFinal.aguardando_exames ? (
                  <div className="p-2.5 bg-amber-100 text-amber-700 rounded-xl">
                    <Clock className="w-7 h-7" />
                  </div>
                ) : (
                  <div className="p-2.5 bg-primary-100 text-primary-700 rounded-xl">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                )}
                <div>
                  <h3
                    className={`text-lg font-bold ${
                      resultadoFinal.aguardando_exames
                        ? 'text-amber-900'
                        : 'text-primary-900'
                    }`}
                  >
                    {resultadoFinal.aguardando_exames
                      ? 'Anamnese Salva • Aguardando Exames Complementares'
                      : 'Atendimento Realizado e ASO Assinado!'}
                  </h3>
                  <p
                    className={`text-xs sm:text-sm mt-0.5 ${
                      resultadoFinal.aguardando_exames
                        ? 'text-amber-800'
                        : 'text-primary-700'
                    }`}
                  >
                    {resultadoFinal.message}
                  </p>
                </div>
              </div>

              {resultadoFinal.exames_pendentes &&
                resultadoFinal.exames_pendentes.length > 0 && (
                  <div className="p-3.5 bg-white/80 rounded-xl border border-amber-200 text-xs space-y-1.5">
                    <span className="font-bold text-amber-900 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      Exames Complementares a Concluir para Auto-Emissão do ASO:
                    </span>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {resultadoFinal.exames_pendentes.map((exp, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-900 text-2xs font-semibold border border-amber-300"
                        >
                          {exp}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {resultadoFinal.aso_url && (
                <div className="pt-2 flex flex-wrap items-center gap-3">
                  <a
                    href={resultadoFinal.aso_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl shadow-md transition-all text-sm cursor-pointer"
                  >
                    <FileText className="w-4 h-4" />
                    Visualizar ASO Assinado Digitalmente
                    <ExternalLink className="w-4 h-4 opacity-80" />
                  </a>
                </div>
              )}
            </div>
          )}

          {/* SEÇÃO 1: QUEM É O COLABORADOR & DADOS DO AGENDAMENTO */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-xs">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200/80">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                <User className="w-4 h-4 text-primary-600" />
                <span>Identificação do Colaborador</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-800 border border-primary-200">
                  <Calendar className="w-3 h-3 text-primary-600" />
                  {formatDateBR(atendimento.data_agendamento)}
                  {atendimento.hora_agendamento ? ` às ${atendimento.hora_agendamento}` : ''}
                </span>
                {atendimento.pedido_codigo && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    Pedido: {atendimento.pedido_codigo}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {/* Coluna 1: Dados Pessoais */}
              <div className="space-y-1">
                <p className="text-xs text-slate-400 font-medium">Nome do Colaborador</p>
                <p className="font-bold text-slate-900 text-base leading-tight">
                  {colaborador?.nome || '—'}
                </p>
                <div className="pt-1 text-xs text-slate-600 space-y-0.5">
                  <p>
                    <strong className="text-slate-500">CPF:</strong> {formatCpf(colaborador?.cpf) || '—'}
                    {colaborador?.rg && <span className="ml-2">• <strong className="text-slate-500">RG:</strong> {colaborador.rg}</span>}
                  </p>
                  <p>
                    <strong className="text-slate-500">Nascimento:</strong> {formatDateBR(colaborador?.data_nascimento)}
                    {colaborador?.idade !== undefined && colaborador?.idade !== null && (
                      <span className="text-slate-500 font-medium"> ({colaborador.idade} anos)</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Coluna 2: Cargo e Setor */}
              <div className="space-y-1">
                <p className="text-xs text-slate-400 font-medium">Função & Ocupação</p>
                <p className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-primary-500 shrink-0" />
                  {colaborador?.cargo || 'Cargo não informado'}
                </p>
                <div className="pt-1 text-xs text-slate-600 space-y-0.5">
                  <p>
                    <strong className="text-slate-500">Setor:</strong> {colaborador?.setor || 'Não informado'}
                  </p>
                  {colaborador?.data_admissao && (
                    <p>
                      <strong className="text-slate-500">Admissão:</strong> {formatDateBR(colaborador.data_admissao)}
                    </p>
                  )}
                </div>
              </div>

              {/* Coluna 3: Empresa Empregadora */}
              <div className="space-y-1">
                <p className="text-xs text-slate-400 font-medium">Empresa Contratante</p>
                <p className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                  <Building className="w-4 h-4 text-primary-600 shrink-0" />
                  {colaborador?.empresa?.nome || '—'}
                </p>
                <div className="pt-1 text-xs text-slate-600">
                  <p>
                    <strong className="text-slate-500">CNPJ:</strong> {formatCnpj(colaborador?.empresa?.cnpj) || '—'}
                  </p>
                  <p className="mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-bold uppercase bg-slate-200/80 text-slate-700">
                      {atendimento.tipo_exame}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SEÇÃO 2: EXAMES QUE ELE TEM PRA FAZER */}
          <div className="rounded-2xl border border-primary-200/80 bg-gradient-to-br from-primary-50/50 via-white to-slate-50 p-5 shadow-xs space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-primary-100 text-primary-700">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Exames Previstos para este Colaborador
                  </h3>
                  <p className="text-xs text-slate-500">
                    Relação de exames clínicos e complementares definidos no PCMSO
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-800 border border-primary-200">
                  {listaExames.length} {listaExames.length === 1 ? 'Exame' : 'Exames'}
                </span>
              </div>
            </div>

            {/* Grid dos Exames */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {listaExames.map((exame) => {
                const isClinico = exame.tipo === 'CLINICO';
                const isRealizado =
                  exame.status === 'REALIZADO' ||
                  exame.status === 'CONCLUIDO' ||
                  Boolean(exame.resultado_id) ||
                  Boolean(exame.resultado_url);

                return (
                  <div
                    key={`${exame.id}_${exame.nome}`}
                    className={`p-3.5 rounded-xl border transition-all relative flex flex-col justify-between ${
                      isClinico
                        ? 'bg-primary-50/60 border-primary-200 shadow-2xs'
                        : isRealizado
                        ? 'bg-white border-slate-200 shadow-2xs'
                        : 'bg-white border-primary-200 shadow-2xs'
                    }`}
                  >
                    {/* Status do Exame posicionado no canto superior direito alinhado um pouco para fora */}
                    <div className="absolute -top-2.5 -right-1.5 z-10">
                      {isRealizado ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-primary-100 text-primary-800 border border-primary-200 shadow-xs whitespace-nowrap">
                          <CheckCircle className="w-3 h-3 text-primary-600 shrink-0" />
                          Realizado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold bg-amber-50 text-amber-800 border border-amber-200 shadow-xs whitespace-nowrap">
                          <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                          {isClinico ? 'Em Atendimento' : 'Pendente'}
                        </span>
                      )}
                    </div>

                    <div>
                      {/* Título alinhado com o ícone e com espaçamento à direita para quebrar sem invadir a label */}
                      <div className="flex items-start gap-2 pr-32 mb-1">
                        {isClinico ? (
                          <Stethoscope className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                        ) : exame.laboratorial ? (
                          <FlaskConical className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                        ) : (
                          <Activity className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                        )}
                        <h4 className="text-xs font-bold text-slate-900 leading-snug break-words">
                          {exame.nome}
                        </h4>
                      </div>
                    </div>

                    {exame.resultado_id || exame.resultado_url ? (
                      <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleVisualizarLaudo(exame.resultado_id, exame.agendamento_id)}
                          disabled={visualizandoLaudoId === (exame.resultado_id || exame.agendamento_id)}
                          className="inline-flex items-center gap-1.5 text-2xs font-bold text-primary-600 hover:text-primary-700 hover:underline cursor-pointer disabled:opacity-50"
                        >
                          {visualizandoLaudoId === (exame.resultado_id || exame.agendamento_id) ? (
                            <>
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              <span>Abrindo...</span>
                            </>
                          ) : (
                            <>
                              <Eye className="w-3.5 h-3.5" />
                              <span>Ver Laudo Anexado</span>
                            </>
                          )}
                        </button>
                      </div>
                    ) : !isClinico ? (
                      <div className="pt-2 mt-2 border-t border-slate-100">
                        <span className="text-2xs text-slate-500 italic">
                          Laudo pendente de envio pela clínica
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Riscos Ocupacionais (Se cadastrados no PCMSO do cargo) */}
            {temRiscos && riscos && (
              <div className="mt-3 p-3 bg-slate-100/70 rounded-xl border border-slate-200/80 text-xs">
                <span className="font-bold text-slate-700 block mb-1.5 text-2xs uppercase tracking-wider">
                  Riscos Ocupacionais Associados à Função (NR-07):
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {riscos.fisico?.map((r, i) => (
                    <span key={`fisico_${i}`} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-2xs font-medium border border-amber-200">
                      ⚡ Físico: {r}
                    </span>
                  ))}
                  {riscos.quimico?.map((r, i) => (
                    <span key={`quimico_${i}`} className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-900 text-2xs font-medium border border-rose-200">
                      🧪 Químico: {r}
                    </span>
                  ))}
                  {riscos.biologico?.map((r, i) => (
                    <span key={`biologico_${i}`} className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-900 text-2xs font-medium border border-purple-200">
                      🦠 Biológico: {r}
                    </span>
                  ))}
                  {riscos.ergonomico?.map((r, i) => (
                    <span key={`ergo_${i}`} className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-900 text-2xs font-medium border border-primary-200">
                      🧘 Ergonômico: {r}
                    </span>
                  ))}
                  {riscos.acidente?.map((r, i) => (
                    <span key={`acid_${i}`} className="px-2 py-0.5 rounded-md bg-orange-100 text-orange-900 text-2xs font-medium border border-orange-200">
                      ⚠️ Acidente: {r}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Validação Obrigatória do Certificado A1 */}
          {exigeCertificadoA1 && !medico.has_valid_certificate && (
            <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-300 text-amber-900 flex items-start justify-between gap-4 shadow-xs">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-sm text-amber-900">
                    Certificado Digital A1 Obrigatório Não Vinculado
                  </h4>
                  <p className="text-xs text-amber-700 mt-1">
                    Você pode preencher os dados clínicos e a anamnese normalmente. Para concluir o atendimento e assinar digitalmente o ASO com validade jurídica pelo ICP-Brasil, vincule seu arquivo (.pfx).
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onOpenCertificadoModal}
                className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl text-xs shrink-0 shadow-sm transition-all cursor-pointer"
              >
                Vincular Certificado A1
              </button>
            </div>
          )}

          {/* FORMULÁRIO CLÍNICO (TRIAGEM, ANAMNESE E PARECER) */}
          {!resultadoFinal && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <fieldset disabled={isReadOnly} className={`contents ${isReadOnly ? 'opacity-90' : ''}`}>
              {/* 1. Triagem e Sinais Vitais */}
              <div className="space-y-3 p-5 rounded-2xl border border-slate-200 bg-white shadow-xs">
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                    <Activity className="w-4 h-4 text-primary-600" />
                    Triagem e Sinais Vitais
                  </div>
                  {imcInfo && (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${imcInfo.badgeColor}`}>
                      IMC: {imcInfo.imc} • {imcInfo.classificacao}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 pt-1">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      PA Sistólica
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={sistolica}
                        onChange={(e) => setSistolica(e.target.value)}
                        placeholder="120"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        mmHg
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      PA Diastólica
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={diastolica}
                        onChange={(e) => setDiastolica(e.target.value)}
                        placeholder="80"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        mmHg
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-center gap-1">
                      <Heart className="w-3 h-3 text-rose-500" />
                      FC
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={fc}
                        onChange={(e) => setFc(e.target.value)}
                        placeholder="72"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        bpm
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-center gap-1">
                      FR
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={fr}
                        onChange={(e) => setFr(e.target.value)}
                        placeholder="16"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        rpm
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-center gap-1">
                      <Thermometer className="w-3 h-3 text-amber-500" />
                      Temp.
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={temperatura}
                        onChange={(e) => setTemperatura(e.target.value)}
                        placeholder="36.5"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        °C
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-center gap-1">
                      <Weight className="w-3 h-3 text-primary-500" />
                      Peso
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={peso}
                        onChange={(e) => setPeso(e.target.value)}
                        placeholder="70.5"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        kg
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-center gap-1">
                      <Ruler className="w-3 h-3 text-primary-500" />
                      Altura
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={altura}
                        onChange={(e) => setAltura(e.target.value)}
                        placeholder="175"
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg text-center font-medium focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <span className="text-[10px] text-slate-400 block text-center mt-0.5">
                        cm
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Anamnese Clínica Ocupacional */}
              {headers.length > 0 && (
                <div className="space-y-4 p-5 rounded-2xl border border-slate-200 bg-white shadow-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                      <FileText className="w-4 h-4 text-primary-600" />
                      Anamnese Clínica Ocupacional
                    </div>

                    <button
                      type="button"
                      onClick={handleMarcarTodasNao}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                      title="Marcar todas as opções como 'Não' / Sem queixas"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-primary-600" />
                      <span>Preencher todas com 'Não'</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {headers.map((h) => (
                      <div
                        key={h.id}
                        className="p-4 rounded-xl border border-slate-200/90 bg-slate-50/50 space-y-3"
                      >
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                          {h.titulo_exibicao || h.descricao}
                        </h4>

                        <div className="space-y-2.5">
                          {h.ficha_questao?.map((q) => {
                            const current = respostas[q.id] || { resposta: 0, complemento: '' };
                            return (
                              <div
                                key={q.id}
                                className="p-3 bg-white rounded-xl border border-slate-200/80 space-y-2 shadow-2xs"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-slate-800 leading-snug">
                                    {q.questao}
                                  </span>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleRespostaChange(q.id, 0)}
                                      className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                        current.resposta === 0
                                          ? 'bg-slate-800 text-white border-slate-800 shadow-xs'
                                          : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                                      }`}
                                    >
                                      Não
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRespostaChange(q.id, 1)}
                                      className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                        current.resposta === 1
                                          ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                                          : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                                      }`}
                                    >
                                      Sim
                                    </button>
                                  </div>
                                </div>

                                {current.resposta === 1 && (
                                  <div className="pt-1.5 animate-fadeIn">
                                    <input
                                      type="text"
                                      value={current.complemento}
                                      onChange={(e) => handleComplementoChange(q.id, e.target.value)}
                                      placeholder="Descreva detalhes, tempo de evolução ou medicação em uso..."
                                      className="w-full px-3 py-1.5 text-xs border border-amber-300 rounded-lg bg-amber-50/50 focus:ring-1 focus:ring-amber-500 focus:border-amber-500 outline-none"
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Parecer Clínico de Aptidão */}
              {/* 3. Parecer Clínico de Aptidão */}
              {temExamesPendentes && (
                <div className="space-y-2 p-4 rounded-2xl border border-amber-200 bg-amber-50/90 shadow-xs">
                  <div className="flex items-center gap-2 font-bold text-amber-800 text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Atenção: Constam exames complementares listados como pendentes</span>
                  </div>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Você pode registrar o parecer provisório e aguardar os laudos no sistema ("Salvar Dados e Aguardar Exames"), ou caso já tenha conferido os resultados físicos/externos, definir o parecer definitivo abaixo e emitir o ASO.
                  </p>
                </div>
              )}

              <div className="space-y-3 p-5 rounded-2xl border border-slate-200 bg-white shadow-xs">
                <div className="flex items-center gap-2 font-bold text-slate-800 text-sm border-b pb-2">
                  <FileCheck2 className="w-4 h-4 text-primary-600" />
                  <span>Parecer Clínico Ocupacional (Resultado do ASO)</span>
                  <span className="text-red-500 font-bold ml-1">*</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                  {[
                    {
                      id: 'APTO',
                      label: 'APTO',
                      desc: 'Apto para a função sem restrições',
                      color: 'border-primary-500 bg-primary-50/80 text-primary-950',
                      badge: 'bg-primary-600 text-white',
                    },
                    {
                      id: 'APTO_COM_RESTRICAO',
                      label: 'APTO COM RESTRIÇÃO',
                      desc: 'Apto com restrições operacionais',
                      color: 'border-amber-500 bg-amber-50/80 text-amber-950',
                      badge: 'bg-amber-600 text-white',
                    },
                    {
                      id: 'INAPTO_TEMPORARIO',
                      label: 'INAPTO TEMPORÁRIO',
                      desc: 'Inaptidão provisória para reavaliação',
                      color: 'border-orange-500 bg-orange-50/80 text-orange-950',
                      badge: 'bg-orange-600 text-white',
                    },
                    {
                      id: 'INAPTO',
                      label: 'INAPTO',
                      desc: 'Inapto para a função designada',
                      color: 'border-rose-500 bg-rose-50/80 text-rose-950',
                      badge: 'bg-rose-600 text-white',
                    },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setValor(opt.id)}
                      className={`p-3.5 rounded-xl border-2 text-left transition-all relative cursor-pointer ${
                        valor === opt.id
                          ? `${opt.color} shadow-md ring-1 ring-primary-500/20`
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">{opt.label}</span>
                        {valor === opt.id && (
                          <span className={`w-2.5 h-2.5 rounded-full ${opt.badge}`} />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Observações Médicas */}
              <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-xs space-y-2">
                <label className="block text-xs font-semibold text-slate-700">
                  Observações Médicas {valor !== 'APTO' && <span className="text-red-500 ml-1">*</span>}
                </label>
                <FastTextarea
                  rows={3}
                  value={observacao}
                  onValueChange={setObservacao}
                  placeholder="Informações complementares, parecer pericial, recomendações de saúde ou justificativa clínica do parecer..."
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>

              {/* 5. Biometria Facial do Colaborador (Comprovante de Ciência e Assinatura Eletrônica) */}
              {(exigeBiometriaConsulta || biometriaFoto || biometriaColetadaAnteriormente) && (
              <div className="p-5 rounded-2xl border-2 border-primary-200 bg-gradient-to-br from-primary-50/40 via-white to-slate-50 shadow-xs space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary-100 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-primary-100 text-primary-700">
                      <Camera className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-bold text-slate-900">
                          Biometria Facial do Colaborador
                        </h3>
                        {exigeBiometriaConsulta ? (
                          <>
                            <span className="text-red-500 font-bold ml-1">*</span>
                            <span className="px-2 py-0.5 rounded-full text-2xs font-extrabold bg-primary-100 text-primary-800 border border-primary-200 uppercase">
                              Obrigatória
                            </span>
                          </>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-2xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Registrada
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Comprova a ciência e presença física do colaborador, viabilizando a assinatura eletrônica da 2ª via do ASO
                      </p>
                    </div>
                  </div>

                  {biometriaFoto && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-800 border border-primary-300">
                      <CheckCircle className="w-3.5 h-3.5 text-primary-600" />
                      Biometria Coletada
                    </span>
                  )}
                </div>

                {cameraError && (
                  <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>{cameraError}</span>
                  </div>
                )}

                {/* Área da Câmera / Captura / Upload */}
                {isModificacao && biometriaColetadaAnteriormente && !biometriaFoto ? (
                  <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl border border-primary-300 bg-primary-50/70">
                    <div className="relative w-28 h-32 rounded-xl border-2 border-primary-500 bg-slate-100 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-10 h-10 text-primary-400 opacity-50" />
                    </div>
                    <div className="flex-1 space-y-1 text-center sm:text-left">
                      <div className="flex items-center justify-center sm:justify-start gap-1.5 text-primary-900 font-bold text-xs sm:text-sm">
                        <CheckCircle className="w-4 h-4 text-primary-600 shrink-0" />
                        <span>Biometria Facial já registrada no atendimento original</span>
                      </div>
                      <p className="text-2xs sm:text-xs text-primary-800 leading-relaxed">
                        A biometria facial deste colaborador já foi coletada anteriormente na ficha. Não é necessário registrar novamente para editar o atendimento.
                      </p>
                    </div>
                  </div>
                ) : !biometriaFoto ? (
                  <div className="space-y-3">
                    {isCameraActive ? (
                      /* Live Camera Streaming com Moldura Pontilhada Oval */
                      <div className="relative rounded-2xl overflow-hidden bg-slate-950 aspect-[4/3] max-w-md mx-auto flex items-center justify-center border-2 border-primary-400 shadow-xl">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover scale-x-[-1]"
                        />

                        {/* Moldura Oval Pontilhada com Guias de Rosto */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <div className="w-48 h-64 sm:w-56 sm:h-72 rounded-[50%] border-3 border-dashed border-primary-400 bg-primary-500/10 shadow-[0_0_0_9999px_rgba(15,23,42,0.65)] flex flex-col items-center justify-between py-6">
                            <span className="text-[11px] font-bold text-primary-200 bg-slate-900/80 px-2.5 py-0.5 rounded-full border border-primary-400/40">
                              Topo da Cabeça
                            </span>
                            <span className="text-[11px] font-bold text-primary-200 bg-slate-900/80 px-2.5 py-0.5 rounded-full border border-primary-400/40">
                              Queixo aqui
                            </span>
                          </div>
                          <p className="mt-2 text-xs font-bold text-white bg-slate-900/90 px-3 py-1 rounded-full border border-slate-700 shadow-md">
                            Posicione o rosto do colaborador dentro do contorno pontilhado
                          </p>
                        </div>

                        {/* Botões de Ação na Câmera */}
                        <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3 px-4 z-10">
                          <button
                            type="button"
                            onClick={captureSnapshot}
                            className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 active:scale-95 text-white font-bold text-xs sm:text-sm rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition-all ring-2 ring-white/30"
                          >
                            <Camera className="w-4 h-4" />
                            <span>Capturar Foto Facial</span>
                          </button>
                          <button
                            type="button"
                            onClick={stopCamera}
                            className="px-4 py-2.5 bg-slate-800/90 hover:bg-slate-700 text-slate-200 font-semibold text-xs rounded-xl shadow-md cursor-pointer transition-all"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Opções de Inicialização: Câmera ou Upload */
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={startCamera}
                          className="p-5 rounded-2xl border-2 border-dashed border-primary-300 hover:border-primary-500 bg-primary-50/50 hover:bg-primary-50 transition-all flex flex-col items-center justify-center text-center gap-2 group cursor-pointer"
                        >
                          <div className="w-12 h-12 rounded-2xl bg-primary-100 group-hover:bg-primary-200 text-primary-600 flex items-center justify-center transition-colors">
                            <Camera className="w-6 h-6" />
                          </div>
                          <div>
                            <span className="text-xs sm:text-sm font-bold text-primary-900 block">
                              Coletar pela Câmera / Webcam
                            </span>
                            <span className="text-2xs text-primary-600 mt-0.5 block">
                              Abre a câmera com guia pontilhado para enquadramento do rosto
                            </span>
                          </div>
                        </button>

                        <label className="p-5 rounded-2xl border-2 border-dashed border-slate-300 hover:border-primary-400 bg-slate-50/50 hover:bg-primary-50/30 transition-all flex flex-col items-center justify-center text-center gap-2 group cursor-pointer">
                          <div className="w-12 h-12 rounded-2xl bg-slate-100 group-hover:bg-primary-100 text-slate-600 group-hover:text-primary-600 flex items-center justify-center transition-colors">
                            <Upload className="w-6 h-6" />
                          </div>
                          <div>
                            <span className="text-xs sm:text-sm font-bold text-slate-800 group-hover:text-primary-900 block">
                              Anexar Foto da Biometria Facial
                            </span>
                            <span className="text-2xs text-slate-500 group-hover:text-primary-700 mt-0.5 block">
                              Carregar arquivo de imagem salvo no dispositivo (.jpg, .jpeg, .png)
                            </span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Pré-visualização da Imagem Coletada */
                  <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl border border-primary-300 bg-primary-50/70">
                    <div className="relative w-28 h-32 rounded-xl overflow-hidden border-2 border-primary-500 shadow-sm shrink-0 bg-slate-900">
                      <img
                        src={biometriaFoto}
                        alt="Biometria Facial do Colaborador"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            'https://ui-avatars.com/api/?name=Biometria+Coletada&background=random';
                        }}
                      />
                      <div className="absolute bottom-0 inset-x-0 bg-primary-700/90 text-white text-[9px] font-bold py-0.5 text-center">
                        {biometriaTipo === 'WEBCAM' ? 'Captura Webcam' : 'Arquivo Anexo'}
                      </div>
                    </div>
                    <div className="flex-1 space-y-1 text-center sm:text-left">
                      <div className="flex items-center justify-center sm:justify-start gap-1.5 text-primary-900 font-bold text-xs sm:text-sm">
                        <CheckCircle className="w-4 h-4 text-primary-600 shrink-0" />
                        <span>Biometria Facial Registrada com Sucesso</span>
                      </div>
                      <p className="text-2xs sm:text-xs text-primary-800 leading-relaxed">
                        Esta imagem comprova que o colaborador foi examinado presencialmente e tem ciência da 2ª via do ASO. A assinatura eletrônica com carimbo biométrico será impressa no documento.
                      </p>
                      {!(isModificacao && biometriaColetadaAnteriormente) && (
                        <div className="pt-2 flex items-center justify-center sm:justify-start gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setBiometriaFoto(null);
                              startCamera();
                            }}
                          className="px-3 py-1.5 text-xs font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          Tirar outra foto
                        </button>
                        <button
                          type="button"
                          onClick={() => setBiometriaFoto(null)}
                          className="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        >
                          Remover
                        </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}
              </fieldset>

              {/* Rodapé com Assinatura e Conclusão */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200">
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary-600 shrink-0" />
                  <span>
                    ASO assinado digitalmente por{' '}
                    <strong className="text-slate-800">Dr(a). {medico.nome}</strong> (CRM/{medico.uf} {medico.crm})
                  </span>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 sm:flex-none px-4 py-2.5 text-xs sm:text-sm font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 hover:border-primary-400 rounded-xl transition-colors cursor-pointer"
                    disabled={submitting}
                  >
                    Cancelar
                  </button>

                  {isReadOnly ? (
                    <div className="flex items-center gap-2">
                      {!effectiveAsoUrl && (
                        <button
                          type="button"
                          onClick={handleForcarGerarAsoLocal}
                          disabled={gerandoAso || !!readOnly}
                          className={`flex-1 sm:flex-none px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 ${
                            gerandoAso || !!readOnly
                              ? "bg-slate-300 text-slate-500 cursor-not-allowed opacity-70"
                              : "text-white bg-amber-600 hover:bg-amber-700 cursor-pointer"
                          }`}
                          title={readOnly ? "Certificado A1 obrigatório para emitir ASO" : "Geração/assinatura do ASO pendente. Clique para forçar a emissão agora."}
                        >
                          {gerandoAso ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Gerando ASO...</span>
                            </>
                          ) : (
                            <>
                              <FileCheck2 className="w-4 h-4" />
                              <span>Forçar Gerar ASO</span>
                            </>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          onReabrir &&
                          onReabrir(atendimento.agendamento_id || atendimento.id)
                        }
                        disabled={!!readOnly}
                        className={`flex-1 sm:flex-none px-5 py-2.5 text-xs sm:text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 border ${
                          readOnly
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70'
                            : 'text-rose-600 border-rose-200 hover:bg-rose-50 cursor-pointer'
                        }`}
                        title={readOnly ? 'Certificado A1 obrigatório para reabrir' : ''}
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Reabrir Atendimento</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {isReaberto && !effectiveAsoUrl && (
                        <button
                          type="button"
                          onClick={handleForcarGerarAsoLocal}
                          disabled={gerandoAso || !!readOnly}
                          className="px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 cursor-pointer disabled:opacity-50"
                          title="Forçar emissão do ASO diretamente"
                        >
                          {gerandoAso ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Gerando ASO...</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              <span>Forçar Gerar ASO</span>
                            </>
                          )}
                        </button>
                      )}
                      {temExamesPendentes ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => handleSubmit(undefined, true)}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-xs sm:text-sm font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                          >
                            <Clock className="w-4 h-4 text-amber-700" />
                            <span>Salvar Dados e Aguardar Exames</span>
                          </button>
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => handleSubmit(undefined, false)}
                            className="flex-1 sm:flex-none px-6 py-2.5 text-xs sm:text-sm font-bold text-white rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
                          >
                            {submitting ? (
                              <>
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>Processando...</span>
                              </>
                            ) : (
                              <>
                                <FileCheck2 className="w-4 h-4" />
                                <span>Concluir Atendimento e Emitir ASO</span>
                              </>
                            )}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => handleSubmit(undefined, false)}
                          className="flex-1 sm:flex-none px-6 py-2.5 text-xs sm:text-sm font-bold text-white rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
                        >
                          {submitting ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Processando Atendimento...</span>
                            </>
                          ) : (
                            <>
                              <FileCheck2 className="w-4 h-4" />
                              <span>
                                {detalhesCompletos?.valores_salvos
                                  ? (medico.has_valid_certificate ? 'Salvar Alterações e Assinar Novo ASO' : 'Salvar Alterações e Emitir ASO')
                                  : (medico.has_valid_certificate ? 'Concluir Atendimento e Assinar ASO' : 'Concluir Atendimento e Gerar ASO')}
                              </span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </form>
          )}

          {resultadoFinal && (
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-md transition-all cursor-pointer"
              >
                Concluir e Voltar para a Fila
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
